import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, HStack, Icon, FormControl, FormLabel, Input, Select, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Button, SimpleGrid, Stat, StatLabel, StatNumber } from "@chakra-ui/react";
import { FaCalculator } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";

const PropertyToolsInvestmentCalculator: React.FC = () => {
  const [price, setPrice] = useState(550000);
  const [deposit, setDeposit] = useState(110000);
  const [rent, setRent] = useState(520);

  const loanAmount = price - deposit;
  const weeklyRepayment = (loanAmount * 0.06) / 52;
  const yield_ = ((rent * 52) / price) * 100;
  const cashflow = (rent * 52) - (weeklyRepayment * 52);

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/tools/investment-calculator"
        fallbackTitle="Property Investment Calculator"
        fallbackDescription="Calculate property investment returns and strategy"
      />
      <NavbarWithCallToAction />

      <Box bgGradient={`linear(to-r, ${NAVY}, ${PROPERTY_PRIMARY})`} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaCalculator} boxSize={16} />
            <Heading size="3xl" fontWeight="black">Investment Calculator</Heading>
            <Text fontSize="xl">Calculate ROI and cash flow for your investment property</Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12}>
          <VStack spacing={6} align="stretch">
            <Heading size="lg" mb={4} color={NAVY}>Property Details</Heading>

            <FormControl>
              <FormLabel>Purchase Price</FormLabel>
              <Input type="number" value={price} onChange={(e) => setPrice(Number(e.target.value))} />
              <Text fontSize="sm" color="gray.600" mt={1}>${price.toLocaleString()}</Text>
            </FormControl>

            <FormControl>
              <FormLabel>Deposit Amount</FormLabel>
              <Slider value={deposit} min={0} max={price} step={5000} onChange={setDeposit}>
                <SliderTrack><SliderFilledTrack bg={PROPERTY_PRIMARY} /></SliderTrack>
                <SliderThumb />
              </Slider>
              <Text fontSize="sm" color="gray.600" mt={1}>${deposit.toLocaleString()} ({((deposit/price)*100).toFixed(0)}%)</Text>
            </FormControl>

            <FormControl>
              <FormLabel>Weekly Rent</FormLabel>
              <Input type="number" value={rent} onChange={(e) => setRent(Number(e.target.value))} />
              <Text fontSize="sm" color="gray.600" mt={1}>${rent.toLocaleString()}/week</Text>
            </FormControl>

            <FormControl>
              <FormLabel>Interest Rate</FormLabel>
              <Select>
                <option>6.0% p.a.</option>
                <option>5.5% p.a.</option>
                <option>6.5% p.a.</option>
              </Select>
            </FormControl>

            <Button bg={PROPERTY_PRIMARY} color="white" _hover={{ bg: "#0a6459" }} size="lg" transition="all 0.2s">Calculate</Button>
          </VStack>

          <VStack spacing={6} align="stretch">
            <Heading size="lg" mb={4} color={NAVY}>Results</Heading>

            <SimpleGrid columns={2} spacing={6}>
              <Stat bg="#EEF0F7" p={6} borderRadius="lg">
                <StatLabel>Loan Amount</StatLabel>
                <StatNumber fontSize="2xl" color={NAVY}>${loanAmount.toLocaleString()}</StatNumber>
              </Stat>

              <Stat bg="#E1F0EC" p={6} borderRadius="lg">
                <StatLabel>Rental Yield</StatLabel>
                <StatNumber fontSize="2xl" color={PROPERTY_PRIMARY}>{yield_.toFixed(2)}%</StatNumber>
              </Stat>

              <Stat bg="#EEF0F7" p={6} borderRadius="lg">
                <StatLabel>Weekly Repayment</StatLabel>
                <StatNumber fontSize="2xl" color={NAVY}>${weeklyRepayment.toFixed(0)}</StatNumber>
              </Stat>

              <Stat bg={cashflow >= 0 ? "green.50" : "red.50"} p={6} borderRadius="lg">
                <StatLabel>Annual Cash Flow</StatLabel>
                <StatNumber fontSize="2xl" color={cashflow >= 0 ? "green.600" : "red.600"}>
                  ${Math.abs(cashflow).toFixed(0)}
                </StatNumber>
              </Stat>
            </SimpleGrid>

            <Box bg="gray.50" p={6} borderRadius="lg" mt={6}>
              <Heading size="sm" mb={3} color={NAVY}>Investment Summary</Heading>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between"><Text fontSize="sm">Annual Rental Income</Text><Text fontWeight="bold">${(rent * 52).toLocaleString()}</Text></HStack>
                <HStack justify="space-between"><Text fontSize="sm">Annual Loan Repayments</Text><Text fontWeight="bold">${(weeklyRepayment * 52).toFixed(0)}</Text></HStack>
                <HStack justify="space-between"><Text fontSize="sm" color={cashflow >= 0 ? "green.600" : "red.600"}>Net Cash Flow</Text><Text fontWeight="bold" color={cashflow >= 0 ? "green.600" : "red.600"}>{cashflow >= 0 ? "Positive" : "Negative"}</Text></HStack>
              </VStack>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyToolsInvestmentCalculator;
